import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { FileText, MapPin, Video, Palette, CheckCircle } from "lucide-react";

const ProcessTimeline = () => {
  const anim = useScrollAnimation();

  const steps = [
    {
      icon: FileText,
      phase: "Woche 1",
      title: "Konzeptentwicklung & Storyboarding",
      description: "Gemeinsam entwickeln wir die kreative Vision für Ihr Projekt. Wir erstellen ein detailliertes Storyboard und planen jeden Shot.",
      deliverables: ["Kreativkonzept", "Storyboard", "Shot List"]
    },
    {
      icon: MapPin,
      phase: "Woche 2",
      title: "Pre-Production & Location Scouting",
      description: "Wir scouten die perfekten Locations in Mannheim, Heidelberg oder anderen Städten der Region und organisieren die gesamte Produktionslogistik.",
      deliverables: ["Location-Auswahl", "Casting", "Produktionsplan"]
    },
    {
      icon: Video,
      phase: "Woche 3",
      title: "Produktion mit Cinema-Equipment",
      description: "Professioneller Dreh mit Cinema-Objektiven, professionellem Licht, Ton und Stabilisierungssystemen wie Gimbals und Steadycams.",
      deliverables: ["Aufnahmen aller Szenen", "Behind-the-Scenes", "Drohnenaufnahmen"]
    },
    {
      icon: Palette,
      phase: "Woche 4",
      title: "Post-Production & Finishing",
      description: "Schnitt, Color Grading, Sound Design und Visual Effects. Wir verleihen Ihrem Film den professionellen Cinema-Look.",
      deliverables: ["Erster Schnitt", "Farbkorrektur", "Sound Mixing"]
    },
    {
      icon: CheckCircle,
      phase: "Finalisierung",
      title: "Review & Delivery",
      description: "Nach Ihrem Feedback nehmen wir letzte Anpassungen vor und liefern den finalen Film in allen benötigten Formaten.",
      deliverables: ["Finaler Film", "Social Media Versionen", "Alle benötigten Formate"]
    }
  ];

  return (
    <section className="section-padding bg-gradient-to-b from-primary/5 to-background">
      <div
        ref={anim.ref}
        className={`container-custom transition-all duration-1000 ${
          anim.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Unser Produktionsprozess: Von der Idee zum fertigen Film
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Ein transparenter, strukturierter Prozess für beste Ergebnisse. Typische Timeline: 4-8 Wochen für einen Imagefilm.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          {steps.map((step, index) => (
            <div
              key={index}
              className="relative flex gap-8 pb-12 last:pb-0"
            >
              {/* Timeline line */}
              {index !== steps.length - 1 && (
                <div className="absolute left-[31px] top-[64px] w-0.5 h-full bg-primary/20" />
              )}

              {/* Icon */}
              <div className="relative flex-shrink-0 w-16 h-16 rounded-full bg-primary/10 border-4 border-background flex items-center justify-center z-10">
                <step.icon className="w-7 h-7 text-primary" />
              </div>

              {/* Content */}
              <div className="flex-1 bg-background/50 backdrop-blur-xs border border-primary/20 rounded-2xl p-6 hover:border-primary/40 hover:shadow-lg transition-all">
                <div className="mb-2">
                  <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-sm font-semibold rounded-full mb-3">
                    {step.phase}
                  </span>
                </div>
                <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                <p className="text-muted-foreground mb-4">{step.description}</p>
                <div className="flex flex-wrap gap-2">
                  {step.deliverables.map((deliverable, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-muted/50 text-sm rounded-lg"
                    >
                      {deliverable}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center bg-primary/5 border border-primary/20 rounded-2xl p-6 max-w-3xl mx-auto">
          <p className="text-muted-foreground">
            <strong>Flexible Timelines:</strong> Je nach Projektumfang kann die Produktion auch schneller erfolgen. Sprechen Sie uns an für Express-Produktionen oder größere Kampagnen.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
